import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Declare Chart.js on window object
declare global {
  interface Window {
    Chart: any;
  }
}

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/') {
      setCurrentPage('home');
    } else if (path === '/about') {
      setCurrentPage('about');
    } else if (path === '/why-us') {
      setCurrentPage('why-us');
    } else if (path === '/careers') {
      setCurrentPage('careers');
    } else if (path === '/features') {
      setCurrentPage('features');
    } else if (path === '/products') {
      setCurrentPage('products');
    } else if (path === '/products/screener') {
      setCurrentPage('screener');
    } else if (path === '/products/pronunciation') {
      setCurrentPage('pronunciation');
    } else if (path === '/products/screener/vocabulary') {
      setCurrentPage('vocabulary');
    } else if (path === '/products/screener/phonological') {
      setCurrentPage('phonological');
    } else if (path === '/products/screener/alphabetic') {
      setCurrentPage('alphabetic');
    } else if (path === '/products/screener/reading') {
      setCurrentPage('reading');
    } else if (path === '/products/screener/processing') {
      setCurrentPage('processing');
    } else if (path === '/products/screener/memory') {
      setCurrentPage('memory');
    } else if (path === '/products/pronunciation/foundations') {
      setCurrentPage('foundations');
    } else if (path === '/products/pronunciation/fluency') {
      setCurrentPage('fluency');
        } else if (path === '/products/pronunciation/mastery') {
          setCurrentPage('mastery');
        } else if (path === '/products/pronunciation/large-class') {
          setCurrentPage('large-class');
        } else if (path === '/products/pronunciation/small-group') {
          setCurrentPage('small-group');
        } else if (path === '/products/pronunciation/one-on-one') {
          setCurrentPage('one-on-one');
        } else if (path === '/reports') {
          setCurrentPage('reports');
    } else if (path === '/pricing') {
      setCurrentPage('pricing');
    } else if (path === '/team') {
      setCurrentPage('team');
    } else if (path === '/get-started') {
      setCurrentPage('get-started');
        } else if (path === '/demo') {
          setCurrentPage('demo');
        } else if (path === '/demo-request') {
          setCurrentPage('demo-request');
        } else {
          setCurrentPage('home');
        }
  }, []);

  // Initialize Chart.js when reports page is loaded
  useEffect(() => {
    if (currentPage === 'reports') {
      const canvas = document.getElementById('progressChart') as HTMLCanvasElement;
      if (canvas && window.Chart) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          new window.Chart(ctx, {
            type: 'line',
            data: {
              labels: ['Sept 2024', 'Oct 2024', 'Nov 2024', 'Dec 2024', 'Jan 2025', 'Mar 2025'],
              datasets: [{
                label: 'Pronunciation Score (%)',
                data: [45, 53, 62, 71, 78, 86],
                borderColor: '#3182ce',
                backgroundColor: 'rgba(49, 130, 206, 0.1)',
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#3182ce',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 8
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                  ticks: {
                    callback: function(value: any) {
                      return value + '%';
                    }
                  },
                  grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                  }
                },
                x: {
                  grid: {
                    display: false
                  }
                }
              },
              elements: {
                line: {
                  tension: 0.4
                }
              }
            }
          });
        }
      }
    }
  }, [currentPage]);

  const handleNavClick = (page: string) => {
    setCurrentPage(page);
    const path = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', path);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('无法访问麦克风，请检查权限设置');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Stop all tracks
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'features':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Complete Language Learning Features</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Comprehensive AI-powered tools for language screening, pronunciation training, and progress monitoring
                </p>
              </div>

              {/* Feature 1: Universal Language Screener */}
              <div className="mb-6">
                <div className="feature-box">
                  <h2 className="title text-2xl font-bold mb-4">
                    <i className="fas fa-bullseye mr-2"></i>Universal Language Screener
                  </h2>
                  <p className="content text-lg mb-4">
                    <span className="highlight">Next Generation:</span> Comprehensive AI-powered assessment covering 6 clinically validated domains in just 5 minutes.
                  </p>
                  
                  {/* Interactive Assessment Demo */}
                  <div className="mb-6" style={{background: '#ffffff', padding: '2rem', borderRadius: '0.75rem', border: '2px solid #3182ce'}}>
                    <h3 className="title text-xl font-semibold mb-3 text-blue-800">
                      <i className="fas fa-brain mr-2"></i>Try Our Language Assessment Demo
                    </h3>
                    <p className="content text-center mb-4">
                      Complete a quick 2-minute assessment to see how our AI evaluates language skills
                    </p>
                    
                    <div className="flex justify-center mb-4">
                      <button
                        onClick={() => {
                          alert('🎯 Assessment Demo Started!\n\nIn the real version, children would:\n• Complete interactive tasks\n• Answer questions about pictures\n• Play word games\n• Practice reading exercises\n\nOur AI analyzes responses across 6 domains:\n✅ Language & Vocabulary\n✅ Phonological Awareness\n✅ Alphabetic Knowledge\n✅ Reading & Spelling\n✅ Processing Speed\n✅ Memory & Processing\n\nResults: 91% accuracy in 5 minutes!');
                        }}
                        className="btn btn-primary"
                      >
                        <i className="fas fa-rocket mr-2"></i>
                        Start Assessment Demo
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="card text-center">
                        <div className="text-4xl mb-2">🎨</div>
                        <h4 className="font-semibold text-blue-800">Visual Tasks</h4>
                        <p className="text-sm text-gray-600">Picture naming, story comprehension</p>
                      </div>
                      <div className="card text-center">
                        <div className="text-4xl mb-2">🎵</div>
                        <h4 className="font-semibold text-blue-800">Audio Tasks</h4>
                        <p className="text-sm text-gray-600">Rhyming, sound recognition</p>
                      </div>
                      <div className="card text-center">
                        <div className="text-4xl mb-2">📝</div>
                        <h4 className="font-semibold text-blue-800">Interactive Games</h4>
                        <p className="text-sm text-gray-600">Word building, letter matching</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="card">
                      <h4 className="font-semibold text-blue-800 mb-2">Six Assessment Domains</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Language & Vocabulary</li>
                        <li>• Phonological Awareness</li>
                        <li>• Alphabetic Knowledge</li>
                        <li>• Reading & Spelling</li>
                        <li>• Processing Speed</li>
                        <li>• Memory & Processing</li>
                      </ul>
                    </div>
                    <div className="card">
                      <h4 className="font-semibold text-blue-800 mb-2">Clinical Validation</h4>
                      <ul className="text-sm space-y-1">
                        <li>• UCSF Partnership</li>
                        <li>• 15,000+ Children Tested</li>
                        <li>• 91% AI Accuracy</li>
                        <li>• California DOE Approved</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature 2: AI Pronunciation Training */}
              <div className="mb-6">
                <div className="articulatory-box">
                  <h2 className="title text-2xl font-bold mb-4">
                    <i className="fas fa-microphone mr-2"></i>AI Pronunciation Training
                  </h2>
                  <p className="content text-lg mb-4">
                    <span className="highlight">Real-time AI pronunciation training</span> with phoneme-level precision and personalized learning paths.
                  </p>
                  
                  {/* Interactive Recording Demo */}
                  <div className="mb-6" style={{background: '#ffffff', padding: '2rem', borderRadius: '0.75rem', border: '2px solid #805ad5'}}>
                    <h3 className="title text-xl font-semibold mb-3 text-purple-800">
                      <i className="fas fa-microphone mr-2"></i>Try Our Pronunciation Training Demo
                    </h3>
                    <p className="content text-center mb-4">
                      Click the microphone to record yourself saying: <strong>"The quick brown fox jumps over the lazy dog"</strong>
                    </p>
                    
                    <div className="flex justify-center items-center gap-8 mb-4">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className="w-20 h-20 rounded-full border-none text-white text-2xl cursor-pointer flex items-center justify-center transition-all duration-300 shadow-lg"
                        style={{
                          background: isRecording ? '#f56565' : '#805ad5',
                        }}
                        onMouseOver={(e) => {
                          if (!isRecording) {
                            const target = e.target as HTMLButtonElement;
                            target.style.transform = 'scale(1.1)';
                            target.style.background = '#6b46c1';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isRecording) {
                            const target = e.target as HTMLButtonElement;
                            target.style.transform = 'scale(1)';
                            target.style.background = '#805ad5';
                          }
                        }}
                      >
                        {isRecording ? <i className="fas fa-stop"></i> : <i className="fas fa-microphone"></i>}
                      </button>
                      
                      <div className="text-center">
                        <div className="text-2xl font-bold" style={{color: isRecording ? '#f56565' : '#805ad5'}}>
                          {formatTime(recordingTime)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {isRecording ? 'Recording...' : 'Click to start'}
                        </div>
                      </div>
                    </div>

                    {audioUrl && (
                      <div className="text-center">
                        <h4 className="font-semibold text-purple-800 mb-2">🎧 Your Recording:</h4>
                        <audio controls className="w-full max-w-md">
                          <source src={audioUrl} type="audio/wav" />
                          Your browser does not support the audio element.
                        </audio>
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <h5 className="font-semibold text-blue-700 mb-2">🤖 AI Analysis (Demo):</h5>
                          <div className="flex justify-center gap-2 flex-wrap">
                            <span className="phoneme-correct">"the" - ✅ Correct</span>
                            <span className="phoneme-needs-work">"quick" - ⚠️ Needs work</span>
                            <span className="phoneme-improving">"brown" - 🔄 Improving</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            💡 <strong>Tip:</strong> Focus on the "qu" sound in "quick" - place your tongue behind your teeth
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Multi-Level Curriculum System */}
                  <h3 className="text-lg font-semibold mb-3 text-purple-800">
                    <i className="fas fa-graduation-cap mr-1"></i>Multi-Level Curriculum System
                  </h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="course-card course-beginner">
                      <div className="bg-green-100 p-2">
                        <h4 className="font-semibold text-green-800">Level 1: Foundations</h4>
                      </div>
                      <div className="p-2">
                        <div className="text-xs mb-1">
                          <span className="badge bg-green-200 text-green-800">Beginner</span>
                        </div>
                        <ul className="text-xs list-disc pl-4 space-y-1">
                          <li>Basic vowel and consonant sounds</li>
                          <li>Simple word pronunciation</li>
                          <li>Phonemic awareness games</li>
                        </ul>
                      </div>
                    </div>
                    <div className="course-card course-intermediate">
                      <div className="bg-blue-100 p-2">
                        <h4 className="font-semibold text-blue-800">Level 2: Fluency</h4>
                      </div>
                      <div className="p-2">
                        <div className="text-xs mb-1">
                          <span className="badge bg-blue-200 text-blue-800">Intermediate</span>
                        </div>
                        <ul className="text-xs list-disc pl-4 space-y-1">
                          <li>Complex sound combinations</li>
                          <li>Multi-syllable words</li>
                          <li>Sentence-level pronunciation</li>
                        </ul>
                      </div>
                    </div>
                    <div className="course-card course-advanced">
                      <div className="bg-purple-100 p-2">
                        <h4 className="font-semibold text-purple-800">Level 3: Mastery</h4>
                      </div>
                      <div className="p-2">
                        <div className="text-xs mb-1">
                          <span className="badge bg-purple-200 text-purple-800">Advanced</span>
                        </div>
                        <ul className="text-xs list-disc pl-4 space-y-1">
                          <li>Difficult sound distinctions</li>
                          <li>Natural flow & rhythm</li>
                          <li>Contextual communication</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Closed Loop Feedback System */}
                  <h3 className="text-lg font-semibold mb-3 text-purple-800">
                    <i className="fas fa-sync-alt mr-1"></i>Closed Loop Feedback System
                  </h3>
                  <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 mb-4">
                    <div className="grid grid-cols-5 gap-1">
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-microphone text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">Speech Input</div>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <i className="fas fa-arrow-right text-yellow-500"></i>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-brain text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">AI Analysis</div>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <i className="fas fa-arrow-right text-yellow-500"></i>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-comment-dots text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">Instant Feedback</div>
                      </div>
                    </div>
                    <div className="flex justify-center mt-2">
                      <i className="fas fa-arrow-down text-yellow-500 mx-4"></i>
                    </div>
                    <div className="grid grid-cols-5 gap-1 mt-2">
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-chart-line text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">Track Progress</div>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <i className="fas fa-arrow-left text-yellow-500"></i>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-gamepad text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">Practice</div>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <i className="fas fa-arrow-left text-yellow-500"></i>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-yellow-200 flex items-center justify-center">
                          <i className="fas fa-book text-yellow-700"></i>
                        </div>
                        <div className="text-xs mt-1">Custom Lesson</div>
                      </div>
                    </div>
                  </div>

                  {/* Phoneme Analysis Example */}
                  <div className="articulatory-box p-3 mb-3">
                    <h4 className="font-semibold text-purple-800">Phoneme Analysis Example:</h4>
                    <div className="flex items-center mt-1">
                      <div>
                        <span className="phoneme-correct">æ - correct</span>
                        <span className="phoneme-needs-work">θ - needs work</span>
                        <span className="phoneme-improving">ʃ - improving</span>
                        <span className="phoneme-correct">k - correct</span>
                        <p className="text-xs mt-1">"th" sound (θ): Place the tip of your tongue between your teeth. Gently blow air through without vibrating your vocal cords.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature 3: Progress Analytics */}
              <div className="mb-6">
                <div className="upsell-box">
                  <h2 className="title text-2xl font-bold mb-4">
                    <i className="fas fa-chart-line mr-2"></i>Real-time Progress Analytics
                  </h2>
                  <p className="content text-lg mb-4">
                    <span className="highlight">Comprehensive monitoring</span> with actionable insights for teachers and parents.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="card">
                      <h4 className="font-semibold text-teal-700 mb-2">Teacher Dashboard</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Class-wide progress overview</li>
                        <li>• Individual student reports</li>
                        <li>• Intervention recommendations</li>
                        <li>• Curriculum alignment tools</li>
                      </ul>
                    </div>
                    <div className="card">
                      <h4 className="font-semibold text-teal-700 mb-2">Parent Portal</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Child's learning journey</li>
                        <li>• Home practice activities</li>
                        <li>• Progress celebrations</li>
                        <li>• Communication with teachers</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'pricing':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Simple, Transparent Pricing</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Choose the perfect plan for your child's learning journey. No hidden fees, no surprises.
                </p>
              </div>
              
              <div className="pricing">
                <div className="pricing-card">
                  <div className="pricing-header">
                    <h3 className="pricing-name">Starter</h3>
                    <div className="pricing-price">$50</div>
                    <div className="pricing-period">per child / year</div>
                  </div>
                  <ul className="pricing-features">
                    <li>Universal Language Screener</li>
                    <li>6 Assessment Domains</li>
                    <li>Basic Progress Tracking</li>
                    <li>Parent Dashboard</li>
                    <li>Email Support</li>
                  </ul>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('screener'); }} className="btn btn-outline">Get Started</a>
                </div>
                
                <div className="pricing-card featured">
                  <div className="pricing-header">
                    <h3 className="pricing-name">Premium</h3>
                    <div className="pricing-price">$200</div>
                    <div className="pricing-period">per child / year</div>
                  </div>
                  <ul className="pricing-features">
                    <li>Everything in Starter</li>
                    <li>AI Pronunciation Training</li>
                    <li>Real-time Analytics</li>
                    <li>Teacher Collaboration Tools</li>
                    <li>Priority Support</li>
                    <li>Advanced Reporting</li>
                  </ul>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('products'); }} className="btn btn-primary">Most Popular</a>
                </div>
                
                <div className="pricing-card">
                  <div className="pricing-header">
                    <h3 className="pricing-name">Pronunciation Add-on</h3>
                    <div className="pricing-price">$100</div>
                    <div className="pricing-period">per child / year</div>
                  </div>
                  <ul className="pricing-features">
                    <li>Advanced Speech Training</li>
                    <li>Phoneme-level Analysis</li>
                    <li>Voice Recognition Technology</li>
                    <li>Accent Improvement Tools</li>
                    <li>Progress Tracking</li>
                  </ul>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-outline">Add On</a>
                </div>
              </div>

              <div className="mt-8 text-center">
                <div className="upsell-box p-6 flex items-center">
                  <div className="w-3/4">
                    <p className="text-base font-semibold">Complete Learning Ecosystem: <span className="highlight">Screening → Assessment → Personalized Training → Ongoing Monitoring</span></p>
                    <p className="text-sm">Powerful closed-loop system with continuous feedback and adapting difficulty creates measurable long-term improvement</p>
                  </div>
                  <div className="w-1/4">
                    <div className="text-center text-teal-700">
                      <div className="text-lg font-bold">$200/year</div>
                      <div className="text-xs">Natural upsell opportunity</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'team':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Our Team</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Meet the passionate team of researchers, clinicians, and technologists 
                  working to transform language learning for children.
                </p>
              </div>

              {/* Core Team */}
              <div className="grid grid-cols-3 gap-8 mb-12">
                <div className="card text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <i className="fas fa-user-tie text-blue-700 text-3xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Jiachen Lian</h3>
                  <p className="text-blue-700 font-semibold mb-2">CEO & COO</p>
                  <p className="content text-sm mb-3">
                    5th-Year EECS PhD at UC Berkeley, Berkeley Speech Group
                  </p>
                  <div className="badge bg-blue-200 text-blue-800">Leadership</div>
                </div>
                
                <div className="card text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <i className="fas fa-laptop-code text-green-700 text-3xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Cheol Jun Cho</h3>
                  <p className="text-green-700 font-semibold mb-2">CTO & CPO</p>
                  <p className="content text-sm mb-3">
                    5th-Year EECS PhD at UC Berkeley, Berkeley Speech Group
                  </p>
                  <div className="badge bg-green-200 text-green-800">Technology</div>
                </div>
                
                <div className="card text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <i className="fas fa-user-md text-purple-700 text-3xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Dr. Maria Luisa Gorno Tempini</h3>
                  <p className="text-purple-700 font-semibold mb-2">Clinical Advisor</p>
                  <p className="content text-sm mb-3">
                    Director, UCSF Memory and Aging Center & Dyslexia Center
                  </p>
                  <div className="badge bg-purple-200 text-purple-800">Clinical</div>
                </div>
              </div>

              {/* Academic Leadership */}
              <div className="card mb-8">
                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mr-6">
                    <i className="fas fa-university text-yellow-700 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="title text-2xl font-semibold mb-2">Academic Leadership</h3>
                    <p className="content text-lg mb-4">
                      <strong>Prof. Gopala Anumanchipalli</strong> - EECS Professor at UC Berkeley, 
                      Robert E. and Beverly A. Brooks Assistant Professor in Electrical Engineering 
                      and Computer Sciences at UC Berkeley, and Department of Neurosurgery at UCSF, 
                      Berkeley Speech Group lead
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <div className="badge bg-yellow-200 text-yellow-800">UC Berkeley</div>
                      <div className="badge bg-yellow-200 text-yellow-800">UCSF</div>
                      <div className="badge bg-yellow-200 text-yellow-800">Research Lead</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategic Partnerships */}
              <div className="card mb-8">
                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mr-6">
                    <i className="fas fa-handshake text-teal-700 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="title text-2xl font-semibold mb-2">Strategic Partnerships</h3>
                    <p className="content text-lg mb-4">
                      Our team leverages the Multitudes Universal Screening Program with unlimited support 
                      from research and deployment schools, government agencies, education specialists, 
                      medical doctors, and SLPs.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <i className="fas fa-university text-teal-500 mr-2"></i>
                        <span className="text-sm">Research Schools</span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-landmark text-teal-500 mr-2"></i>
                        <span className="text-sm">Government Support</span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-chalkboard text-teal-500 mr-2"></i>
                        <span className="text-sm">Education Specialists</span>
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-stethoscope text-teal-500 mr-2"></i>
                        <span className="text-sm">Medical Practitioners</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* The Winning Formula */}
              <div className="card text-center">
                <h3 className="title text-2xl font-bold mb-6">The Winning Formula</h3>
                <div className="flex justify-center items-center flex-wrap gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="fas fa-laptop-code text-blue-700 text-2xl"></i>
                    </div>
                    <p className="font-semibold">Engineers</p>
                    <p className="text-xs text-gray-600">UC Berkeley AI</p>
                  </div>
                  <div className="text-2xl text-gray-400">+</div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="fas fa-stethoscope text-green-700 text-2xl"></i>
                    </div>
                    <p className="font-semibold">Doctors</p>
                    <p className="text-xs text-gray-600">UCSF Clinical</p>
                  </div>
                  <div className="text-2xl text-gray-400">+</div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="fas fa-chalkboard-teacher text-purple-700 text-2xl"></i>
                    </div>
                    <p className="font-semibold">Educators</p>
                    <p className="text-xs text-gray-600">K-12 Experts</p>
                  </div>
                  <div className="text-2xl text-gray-400">+</div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="fas fa-briefcase text-amber-700 text-2xl"></i>
                    </div>
                    <p className="font-semibold">Operators</p>
                    <p className="text-xs text-gray-600">Growth Leaders</p>
                  </div>
                  <div className="text-2xl text-gray-400">=</div>
                  <div className="text-center px-4 bg-blue-700 rounded-lg py-2 text-white">
                    <p className="font-bold">The Only Team</p>
                    <p className="text-sm">capable of winning this market</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'get-started':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Get Started</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Ready to begin your child's language learning journey? 
                  Start with our free trial today.
                </p>
              </div>
            </div>
          </div>
        );
      case 'demo':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Watch Demo</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  See LinguaNest in action! Watch our interactive demo to understand 
                  how our AI-powered platform works.
                </p>
              </div>
            </div>
          </div>
        );
      case 'products':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Our Products</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Choose the perfect solution for your language learning needs
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="card text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-blue-200 flex items-center justify-center mb-4">
                    <i className="fas fa-bullseye text-blue-700 text-3xl"></i>
                  </div>
                  <h2 className="title text-2xl font-bold mb-4">Universal Language Screener</h2>
                  <p className="content mb-4">
                    Comprehensive AI-powered assessment covering 6 clinically validated domains in just 5 minutes.
                  </p>
                  <div className="mb-4">
                    <h3 className="font-semibold text-blue-800 mb-2">Assessment Domains:</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Language & Vocabulary
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Phonological Awareness
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Alphabetic Knowledge
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Reading & Spelling
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Processing Speed
                      </div>
                      <div className="flex items-center">
                        <i className="fas fa-check text-green-600 mr-2"></i>
                        Memory & Processing
                      </div>
                    </div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('screener'); }} className="btn btn-primary">
                    <i className="fas fa-arrow-right mr-2"></i>
                    Explore Screener
                  </a>
                </div>
                
                <div className="card text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-purple-200 flex items-center justify-center mb-4">
                    <i className="fas fa-microphone text-purple-700 text-3xl"></i>
                  </div>
                  <h2 className="title text-2xl font-bold mb-4">AI Pronunciation Training</h2>
                  <p className="content mb-4">
                    Real-time speech training with phoneme-level precision and personalized learning paths.
                  </p>
                  <div className="mb-4">
                    <h3 className="font-semibold text-purple-800 mb-2">Training Levels:</h3>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-green-100 p-2 rounded">
                        <div className="font-semibold text-green-800">Level 1</div>
                        <div className="text-xs">Foundations</div>
                      </div>
                      <div className="bg-blue-100 p-2 rounded">
                        <div className="font-semibold text-blue-800">Level 2</div>
                        <div className="text-xs">Fluency</div>
                      </div>
                      <div className="bg-purple-100 p-2 rounded">
                        <div className="font-semibold text-purple-800">Level 3</div>
                        <div className="text-xs">Mastery</div>
                      </div>
                    </div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-primary">
                    <i className="fas fa-arrow-right mr-2"></i>
                    Explore Training
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      case 'screener':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Universal Language Screener</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Comprehensive AI-powered assessment covering 6 clinically validated domains
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('vocabulary')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-200 flex items-center justify-center mb-4">
                    <i className="fas fa-book text-blue-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Language & Vocabulary</h3>
                  <p className="text-sm text-gray-600">Word knowledge, semantic understanding, and expressive language skills</p>
                </div>
                
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('phonological')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-200 flex items-center justify-center mb-4">
                    <i className="fas fa-volume-up text-green-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Phonological Awareness</h3>
                  <p className="text-sm text-gray-600">Sound recognition, rhyming, and phonemic awareness skills</p>
                </div>
                
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('alphabetic')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-yellow-200 flex items-center justify-center mb-4">
                    <i className="fas fa-font text-yellow-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Alphabetic Knowledge</h3>
                  <p className="text-sm text-gray-600">Letter recognition, letter-sound correspondence, and alphabet skills</p>
                </div>
                
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('reading')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-purple-200 flex items-center justify-center mb-4">
                    <i className="fas fa-book-open text-purple-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Reading & Spelling</h3>
                  <p className="text-sm text-gray-600">Word reading, comprehension, and spelling abilities</p>
                </div>
                
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('processing')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-200 flex items-center justify-center mb-4">
                    <i className="fas fa-tachometer-alt text-red-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Processing Speed</h3>
                  <p className="text-sm text-gray-600">Rapid naming, visual processing, and cognitive speed</p>
                </div>
                
                <div className="card text-center cursor-pointer" onClick={() => handleNavClick('memory')}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-teal-200 flex items-center justify-center mb-4">
                    <i className="fas fa-brain text-teal-700 text-2xl"></i>
                  </div>
                  <h3 className="title text-xl font-semibold mb-2">Memory & Processing</h3>
                  <p className="text-sm text-gray-600">Working memory, attention, and cognitive processing</p>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('reports'); }} className="btn btn-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  View Sample Report
                </a>
              </div>
            </div>
          </div>
        );
      case 'pronunciation':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">AI Pronunciation Training</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Real-time speech training with phoneme-level precision and personalized learning paths
                </p>
              </div>
              
              {/* Teaching Modes */}
              <div className="mb-8">
                <h2 className="title text-2xl font-bold mb-4 text-center">Teaching Modes</h2>
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="card text-center cursor-pointer hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300" onClick={() => handleNavClick('large-class')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-200 flex items-center justify-center mb-4">
                      <i className="fas fa-users text-blue-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">Large Class Mode</h3>
                    <p className="text-sm text-gray-600 mb-4">20-30 students per session, group activities and peer learning</p>
                    <div className="badge bg-blue-200 text-blue-800">Classroom</div>
                  </div>
                  
                  <div className="card text-center cursor-pointer hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300" onClick={() => handleNavClick('small-group')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-green-200 flex items-center justify-center mb-4">
                      <i className="fas fa-user-friends text-green-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">Small Group Mode</h3>
                    <p className="text-sm text-gray-600 mb-4">4-8 students per session, focused practice and collaboration</p>
                    <div className="badge bg-green-200 text-green-800">Intensive</div>
                  </div>
                  
                  <div className="card text-center cursor-pointer hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300" onClick={() => handleNavClick('one-on-one')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-purple-200 flex items-center justify-center mb-4">
                      <i className="fas fa-user text-purple-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">1-on-1 Mode</h3>
                    <p className="text-sm text-gray-600 mb-4">Personalized instruction, maximum attention and progress</p>
                    <div className="badge bg-purple-200 text-purple-800">Premium</div>
                  </div>
                </div>
              </div>
              
              {/* Learning Levels */}
              <div className="mb-8">
                <h2 className="title text-2xl font-bold mb-4 text-center">Learning Levels</h2>
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="card text-center cursor-pointer" onClick={() => handleNavClick('foundations')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-green-200 flex items-center justify-center mb-4">
                      <i className="fas fa-seedling text-green-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">Level 1: Foundations</h3>
                    <p className="text-sm text-gray-600 mb-4">Basic vowel and consonant sounds, simple word pronunciation</p>
                    <div className="badge bg-green-200 text-green-800">Beginner</div>
                  </div>
                  
                  <div className="card text-center cursor-pointer" onClick={() => handleNavClick('fluency')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-200 flex items-center justify-center mb-4">
                      <i className="fas fa-rocket text-blue-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">Level 2: Fluency</h3>
                    <p className="text-sm text-gray-600 mb-4">Complex sound combinations, multi-syllable words</p>
                    <div className="badge bg-blue-200 text-blue-800">Intermediate</div>
                  </div>
                  
                  <div className="card text-center cursor-pointer" onClick={() => handleNavClick('mastery')}>
                    <div className="w-16 h-16 mx-auto rounded-full bg-purple-200 flex items-center justify-center mb-4">
                      <i className="fas fa-crown text-purple-700 text-2xl"></i>
                    </div>
                    <h3 className="title text-xl font-semibold mb-2">Level 3: Mastery</h3>
                    <p className="text-sm text-gray-600 mb-4">Difficult sound distinctions, natural flow & rhythm</p>
                    <div className="badge bg-purple-200 text-purple-800">Advanced</div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('reports'); }} className="btn btn-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  View Progress Reports
                </a>
              </div>
            </div>
          </div>
        );
      case 'large-class':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Large Class Mode</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Interactive group learning for 20-30 students with peer collaboration
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Classroom Features</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-users text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Group Activities</h4>
                        <p className="text-sm text-gray-600">Collaborative pronunciation exercises and peer feedback</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-chalkboard text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Interactive Whiteboard</h4>
                        <p className="text-sm text-gray-600">Visual learning tools and real-time collaboration</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-gamepad text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Gamification</h4>
                        <p className="text-sm text-gray-600">Points, badges, and team competitions</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Demo Interface</h3>
                  <div className="bg-gray-100 rounded-lg p-6 text-center">
                    <div className="w-32 h-32 mx-auto mb-4 relative">
                      <div className="w-full h-full bg-blue-200 rounded-lg flex items-center justify-center">
                        <i className="fas fa-chalkboard text-blue-700 text-4xl"></i>
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-play text-white text-sm"></i>
                      </div>
                    </div>
                    <h4 className="title text-lg font-semibold mb-2">Interactive Classroom</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Experience our AI-powered group learning environment
                    </p>
                    <button className="btn btn-primary">
                      <i className="fas fa-play mr-2"></i>
                      Try Demo
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Training
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-primary">
                  <i className="fas fa-calendar mr-2"></i>
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        );
      case 'small-group':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Small Group Mode</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Focused practice for 4-8 students with intensive collaboration
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Intensive Features</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-user-friends text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Peer Practice</h4>
                        <p className="text-sm text-gray-600">Small group exercises with immediate feedback</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-microphone text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Voice Analysis</h4>
                        <p className="text-sm text-gray-600">Real-time pronunciation feedback for each student</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-chart-line text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Progress Tracking</h4>
                        <p className="text-sm text-gray-600">Individual and group progress monitoring</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Demo Interface</h3>
                  <div className="bg-gray-100 rounded-lg p-6 text-center">
                    <div className="w-32 h-32 mx-auto mb-4 relative">
                      <div className="w-full h-full bg-green-200 rounded-lg flex items-center justify-center">
                        <i className="fas fa-user-friends text-green-700 text-4xl"></i>
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-play text-white text-sm"></i>
                      </div>
                    </div>
                    <h4 className="title text-lg font-semibold mb-2">Small Group Session</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Experience focused group learning with AI feedback
                    </p>
                    <button className="btn btn-primary">
                      <i className="fas fa-play mr-2"></i>
                      Try Demo
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Training
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-primary">
                  <i className="fas fa-calendar mr-2"></i>
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        );
      case 'one-on-one':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">1-on-1 Mode</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Personalized instruction with maximum attention and progress
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Premium Features</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-user text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Personalized AI</h4>
                        <p className="text-sm text-gray-600">Adaptive learning path based on individual needs</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-brain text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Advanced Analytics</h4>
                        <p className="text-sm text-gray-600">Detailed progress analysis and recommendations</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-clock text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Flexible Scheduling</h4>
                        <p className="text-sm text-gray-600">24/7 availability with instant feedback</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Demo Interface</h3>
                  <div className="bg-gray-100 rounded-lg p-6 text-center">
                    <div className="w-32 h-32 mx-auto mb-4 relative">
                      <div className="w-full h-full bg-purple-200 rounded-lg flex items-center justify-center">
                        <i className="fas fa-user text-purple-700 text-4xl"></i>
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-play text-white text-sm"></i>
                      </div>
                    </div>
                    <h4 className="title text-lg font-semibold mb-2">Personalized Session</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Experience one-on-one AI tutoring
                    </p>
                    <button className="btn btn-primary">
                      <i className="fas fa-play mr-2"></i>
                      Try Demo
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Training
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-primary">
                  <i className="fas fa-calendar mr-2"></i>
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        );
      case 'reports':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Sample Reports</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  See how our AI analyzes and presents language assessment results
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Language Screener Report</h3>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold">Student ID: 10254</span>
                      <span className="text-sm text-gray-600">Date: Sept 23, 2025</span>
                    </div>
                    <div className="border-t pt-2">
                      <p className="font-medium mb-2">Overall Performance: <span className="text-green-600">86/100</span></p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Language & Vocabulary:</span>
                            <span className="text-green-600">93%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '93%', backgroundColor: '#48bb78'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Phonological Awareness:</span>
                            <span className="text-yellow-600">82%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '82%', backgroundColor: '#ecc94b'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Reading & Spelling:</span>
                            <span className="text-green-600">90%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '90%', backgroundColor: '#48bb78'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium mb-2">Recommendations:</p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Continue practice with phonological awareness</li>
                      <li>Ready to advance to Level 2 pronunciation training</li>
                      <li>Focus on multi-syllable word practice</li>
                    </ul>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Pronunciation Training Report</h3>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold">Student ID: 10254</span>
                      <span className="text-sm text-gray-600">Date: Sept 23, 2025</span>
                    </div>
                    <div className="border-t pt-2">
                      <p className="font-medium mb-2">Pronunciation Score: <span className="text-blue-600">78/100</span></p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Vowel Sounds:</span>
                            <span className="text-green-600">85%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '85%', backgroundColor: '#48bb78'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Consonant Sounds:</span>
                            <span className="text-yellow-600">72%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '72%', backgroundColor: '#ecc94b'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Rhythm & Stress:</span>
                            <span className="text-red-600">65%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '65%', backgroundColor: '#f56565'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium mb-2">Focus Areas:</p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Continue practice with "th" sounds</li>
                      <li>Work on sentence-level stress patterns</li>
                      <li>Ready to advance to Level 2 consonant blends</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Longitudinal Report */}
              <div className="card">
                <h3 className="title text-2xl font-semibold mb-6 text-center">Longitudinal Progress Report</h3>
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex justify-between mb-4">
                    <span className="font-semibold text-lg">Student ID: 10254 - 6 Month Progress</span>
                    <span className="text-sm text-gray-600">Sept 2024 - Mar 2025</span>
                  </div>
                  
                  {/* Progress Chart */}
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Overall Pronunciation Improvement</h4>
                    <div className="bg-white rounded-lg p-4">
                      <canvas id="progressChart" width="400" height="200"></canvas>
                    </div>
                  </div>
                  
                  {/* Key Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">+41%</div>
                      <div className="text-sm text-gray-600">Overall Improvement</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">6</div>
                      <div className="text-sm text-gray-600">Months Tracked</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">Level 2</div>
                      <div className="text-sm text-gray-600">Current Level</div>
                    </div>
                  </div>
                  
                  {/* Detailed Progress */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Vowel Mastery:</span>
                        <span className="text-green-600">45% → 89% (+44%)</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{width: '89%', backgroundColor: '#48bb78'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Consonant Clarity:</span>
                        <span className="text-green-600">38% → 82% (+44%)</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{width: '82%', backgroundColor: '#48bb78'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Rhythm & Flow:</span>
                        <span className="text-yellow-600">52% → 75% (+23%)</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{width: '75%', backgroundColor: '#ecc94b'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm">
                  <p className="font-medium mb-2">Longitudinal Analysis:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Consistent improvement across all pronunciation domains</li>
                    <li>Most significant gains in vowel and consonant clarity</li>
                    <li>Ready to advance to Level 3 mastery training</li>
                    <li>Recommended focus on rhythm and stress patterns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 'vocabulary':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Language & Vocabulary Assessment</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Comprehensive evaluation of word knowledge, semantic understanding, and expressive language skills
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Assessment Components</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-book text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Word Recognition</h4>
                        <p className="text-sm text-gray-600">Identify and understand common vocabulary words</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-lightbulb text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Semantic Understanding</h4>
                        <p className="text-sm text-gray-600">Comprehend word meanings and relationships</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-comments text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Expressive Language</h4>
                        <p className="text-sm text-gray-600">Use vocabulary in context and conversation</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Sample Tasks</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-3 rounded">
                      <h4 className="font-semibold text-blue-800 mb-2">Picture Naming</h4>
                      <p className="text-sm">Child identifies objects, actions, and concepts in images</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <h4 className="font-semibold text-green-800 mb-2">Word Definitions</h4>
                      <p className="text-sm">Child explains meanings of common words</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                      <h4 className="font-semibold text-purple-800 mb-2">Category Sorting</h4>
                      <p className="text-sm">Child groups words by semantic categories</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('screener'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Screener
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('reports'); }} className="btn btn-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  View Sample Report
                </a>
              </div>
            </div>
          </div>
        );
      case 'phonological':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Phonological Awareness Assessment</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Evaluation of sound recognition, rhyming, and phonemic awareness skills
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Assessment Components</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-volume-up text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Sound Recognition</h4>
                        <p className="text-sm text-gray-600">Identify and distinguish between different sounds</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-music text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Rhyming Skills</h4>
                        <p className="text-sm text-gray-600">Recognize and produce rhyming words</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-puzzle-piece text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Phonemic Awareness</h4>
                        <p className="text-sm text-gray-600">Manipulate individual sounds in words</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Sample Tasks</h3>
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded">
                      <h4 className="font-semibold text-green-800 mb-2">Sound Matching</h4>
                      <p className="text-sm">Child identifies words that start with the same sound</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded">
                      <h4 className="font-semibold text-blue-800 mb-2">Rhyme Recognition</h4>
                      <p className="text-sm">Child identifies which words rhyme with a target word</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                      <h4 className="font-semibold text-purple-800 mb-2">Syllable Counting</h4>
                      <p className="text-sm">Child claps or counts syllables in words</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('screener'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Screener
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('reports'); }} className="btn btn-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  View Sample Report
                </a>
              </div>
            </div>
          </div>
        );
      case 'foundations':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-5">
                <h1 className="title text-4xl font-bold mb-1">Level 1: Foundations</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Basic vowel and consonant sounds, simple word pronunciation
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Learning Objectives</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-volume-up text-green-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800">Vowel Sounds</h4>
                        <p className="text-sm text-gray-600">Master basic vowel sounds (a, e, i, o, u)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-microphone text-blue-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">Consonant Sounds</h4>
                        <p className="text-sm text-gray-600">Learn basic consonant sounds (b, p, m, n, t, d)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-3 mt-1">
                        <i className="fas fa-book text-purple-700"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Simple Words</h4>
                        <p className="text-sm text-gray-600">Pronounce basic CVC words (cat, dog, sun)</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Practice Activities</h3>
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded">
                      <h4 className="font-semibold text-green-800 mb-2">Sound Games</h4>
                      <p className="text-sm">Interactive games to learn individual sounds</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded">
                      <h4 className="font-semibold text-blue-800 mb-2">Word Building</h4>
                      <p className="text-sm">Combine sounds to form simple words</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                      <h4 className="font-semibold text-purple-800 mb-2">Pronunciation Practice</h4>
                      <p className="text-sm">Repeat and practice with AI feedback</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pronunciation'); }} className="btn btn-outline mr-4">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Training
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('reports'); }} className="btn btn-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  View Progress
                </a>
              </div>
            </div>
          </div>
        );
      case 'demo-request':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Enterprise Demonstration Request</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Schedule a comprehensive demonstration of our AI-powered language ecosystem for your organization
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-6">Demonstration Options</h3>
                  <div className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-4 py-4 bg-blue-50 rounded-r-lg">
                      <div className="flex items-center mb-2">
                        <i className="fas fa-building text-blue-600 mr-3 text-lg"></i>
                        <h4 className="font-bold text-blue-800 text-lg">Enterprise Demonstration</h4>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">Comprehensive platform overview for educational institutions and healthcare organizations</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <i className="fas fa-clock text-blue-500 mr-2"></i>
                          <span>45-minute live demonstration</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-users text-blue-500 mr-2"></i>
                          <span>Up to 10 participants</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-chart-line text-blue-500 mr-2"></i>
                          <span>Customized use case scenarios</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-handshake text-blue-500 mr-2"></i>
                          <span>Q&A with our clinical and technical experts</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-l-4 border-green-500 pl-4 py-4 bg-green-50 rounded-r-lg">
                      <div className="flex items-center mb-2">
                        <i className="fas fa-user-tie text-green-600 mr-3 text-lg"></i>
                        <h4 className="font-bold text-green-800 text-lg">Executive Briefing</h4>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">Focused session for decision-makers and administrators</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <i className="fas fa-clock text-green-500 mr-2"></i>
                          <span>30-minute executive overview</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-chart-bar text-green-500 mr-2"></i>
                          <span>ROI and cost-benefit analysis</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-cog text-green-500 mr-2"></i>
                          <span>Implementation roadmap discussion</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-shield-alt text-green-500 mr-2"></i>
                          <span>Compliance and security overview</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-l-4 border-purple-500 pl-4 py-4 bg-purple-50 rounded-r-lg">
                      <div className="flex items-center mb-2">
                        <i className="fas fa-handshake text-purple-600 mr-3 text-lg"></i>
                        <h4 className="font-bold text-purple-800 text-lg">Partnership Discussion</h4>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">Strategic collaboration opportunities for technology partners and integrators</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <i className="fas fa-clock text-purple-500 mr-2"></i>
                          <span>60-minute strategic session</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-code text-purple-500 mr-2"></i>
                          <span>API and integration capabilities</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-dollar-sign text-purple-500 mr-2"></i>
                          <span>Licensing and revenue models</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-rocket text-purple-500 mr-2"></i>
                          <span>Go-to-market strategy alignment</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-6">Request Your Demonstration</h3>
                  <form className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">First Name *</label>
                        <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="John" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Last Name *</label>
                        <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Smith" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Professional Email *</label>
                      <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="john.smith@school.edu" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Organization *</label>
                      <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="University of California, Berkeley" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Job Title *</label>
                      <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Director of Special Education" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Demonstration Type *</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option>Enterprise Demonstration (45 min)</option>
                        <option>Executive Briefing (30 min)</option>
                        <option>Partnership Discussion (60 min)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Preferred Date & Time</label>
                      <input type="datetime-local" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Specific Areas of Interest</label>
                      <textarea rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Please describe your specific use case, current challenges, and what you'd like to see demonstrated..."></textarea>
                    </div>
                    <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition duration-300 transform hover:scale-105">
                      <i className="fas fa-calendar-check mr-2"></i>
                      Schedule Demonstration
                    </button>
                  </form>
                </div>
              </div>
              
              <div className="mt-12 bg-gray-50 rounded-lg p-6">
                <div className="text-center">
                  <h3 className="title text-xl font-semibold mb-4">Why Choose LinguaNest?</h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
                        <i className="fas fa-award text-blue-600 text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-2">Clinically Validated</h4>
                      <p className="text-sm text-gray-600">Developed with UCSF clinical experts and validated across 15,000+ children</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                        <i className="fas fa-rocket text-green-600 text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-2">Scalable Solution</h4>
                      <p className="text-sm text-gray-600">AI-powered platform designed to meet California's 1.15M+ student mandate</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-3">
                        <i className="fas fa-shield-alt text-purple-600 text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-2">Secure & Compliant</h4>
                      <p className="text-sm text-gray-600">HIPAA-compliant with enterprise-grade security and privacy protection</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  <i className="fas fa-info-circle mr-2"></i>
                  Our team will contact you within 24 hours to confirm your demonstration appointment and provide additional details.
                </p>
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className="btn btn-outline">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Return to Homepage
                </a>
              </div>
            </div>
          </div>
        );
      case 'why-us':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Why Choose LinguaNest?</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Superior AI technology and clinical validation that sets us apart from competitors
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                {/* Left Column: Technology Advantage */}
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-6">Proprietary AI Technology</h3>
                  <div className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-4 py-4 bg-blue-50 rounded-r-lg">
                      <h4 className="font-bold text-blue-800 text-lg mb-2">vs. Amira Learning</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-teal-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-teal-800">LinguaNest</span>
                            <span className="text-2xl font-bold text-teal-600">91%</span>
                          </div>
                          <p className="text-sm text-gray-600">Speech Recognition Accuracy</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="bg-teal-500 h-2 rounded-full" style={{width: '91%'}}></div>
                          </div>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-red-800">Amira Learning</span>
                            <span className="text-2xl font-bold text-red-600">73%</span>
                          </div>
                          <p className="text-sm text-gray-600">Speech Recognition Accuracy</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="bg-red-500 h-2 rounded-full" style={{width: '73%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-l-4 border-green-500 pl-4 py-4 bg-green-50 rounded-r-lg">
                      <h4 className="font-bold text-green-800 text-lg mb-2">Clinical Validation</h4>
                      <p className="text-sm text-gray-700 mb-3">Our technology is specifically designed for child language assessment</p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center">
                          <i className="fas fa-check text-green-500 mr-2"></i>
                          <span>Clinically validated with 15,000+ children</span>
                        </li>
                        <li className="flex items-center">
                          <i className="fas fa-check text-green-500 mr-2"></i>
                          <span>UCSF clinical partnership</span>
                        </li>
                        <li className="flex items-center">
                          <i className="fas fa-check text-green-500 mr-2"></i>
                          <span>California DOE approved</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Right Column: Competitive Advantages */}
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-6">Competitive Advantages</h3>
                  <div className="space-y-6">
                    <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                      <h4 className="font-bold text-purple-800 text-lg mb-2">Technology Moat</h4>
                      <p className="text-sm text-gray-700 mb-3">2+ year technology barrier that cannot be replicated</p>
                      <div className="flex items-center">
                        <i className="fas fa-shield-alt text-purple-600 mr-3"></i>
                        <span className="text-sm">Proprietary speech recognition for children</span>
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
                      <h4 className="font-bold text-amber-800 text-lg mb-2">Regulatory Moat</h4>
                      <p className="text-sm text-gray-700 mb-3">Unbreakable barriers to entry</p>
                      <ul className="text-sm space-y-1">
                        <li>• California DOE approval</li>
                        <li>• Clinical validation requirements</li>
                        <li>• Data access at population scale</li>
                      </ul>
                    </div>
                    
                    <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                      <h4 className="font-bold text-indigo-800 text-lg mb-2">Elite Network</h4>
                      <p className="text-sm text-gray-700 mb-3">Top-tier collaborative partnerships</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center">
                          <i className="fas fa-user-md text-indigo-600 mr-2"></i>
                          <span>Medical Doctors</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-comments text-indigo-600 mr-2"></i>
                          <span>SLPs</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-chalkboard-teacher text-indigo-600 mr-2"></i>
                          <span>Educators</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-laptop-code text-indigo-600 mr-2"></i>
                          <span>Researchers</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="space-x-4">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-primary">
                    <i className="fas fa-calendar mr-2"></i>
                    Schedule Demo
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className="btn btn-outline">
                    <i className="fas fa-arrow-left mr-2"></i>
                    Back to Home
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      case 'about':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">About LinguaNest</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Revolutionizing language education through AI-powered technology
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Our Mission</h3>
                  <p className="content mb-4">
                    To democratize access to high-quality language assessment and training through 
                    cutting-edge AI technology, ensuring every child has the opportunity to succeed 
                    in their language learning journey.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Key Statistics</h4>
                    <ul className="text-sm space-y-1">
                      <li>• 15,000+ children assessed</li>
                      <li>• 91% AI accuracy rate</li>
                      <li>• 5-minute assessment time</li>
                      <li>• 1.15M+ students in CA mandate</li>
                    </ul>
                  </div>
                </div>
                
                <div className="card">
                  <h3 className="title text-xl font-semibold mb-4">Our Vision</h3>
                  <p className="content mb-4">
                    A world where language difficulties are identified early and addressed effectively, 
                    creating a level playing field for all children regardless of their background 
                    or circumstances.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Clinical Validation</h4>
                    <ul className="text-sm space-y-1">
                      <li>• UCSF clinical partnership</li>
                      <li>• California DOE approved</li>
                      <li>• HIPAA compliant</li>
                      <li>• Research-backed methodology</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="card mb-8">
                <h3 className="title text-xl font-semibold mb-4 text-center">Our Story</h3>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-lightbulb text-blue-600 text-2xl"></i>
                    </div>
                    <h4 className="font-semibold mb-2">2019-2021</h4>
                    <p className="text-sm text-gray-600">Research began at UC Berkeley with focus on child speech recognition</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-handshake text-green-600 text-2xl"></i>
                    </div>
                    <h4 className="font-semibold mb-2">2022-2023</h4>
                    <p className="text-sm text-gray-600">UCSF partnership established for clinical validation</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-rocket text-purple-600 text-2xl"></i>
                    </div>
                    <h4 className="font-semibold mb-2">2024-2025</h4>
                    <p className="text-sm text-gray-600">California mandate implementation and nationwide expansion</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className="btn btn-primary">
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Home
                </a>
              </div>
            </div>
          </div>
        );
      case 'careers':
        return (
          <div className="section">
            <div className="container">
              <div className="text-center mb-8">
                <h1 className="title text-4xl font-bold mb-1">Join Our Team</h1>
                <div className="accent-bar"></div>
                <p className="content text-lg">
                  Build the future of AI-powered language education with us
                </p>
              </div>
              
              {/* Company Culture Section */}
              <div className="card mb-8">
                <div className="text-center mb-6">
                  <h2 className="title text-2xl font-bold mb-4">Why Work at LinguaNest?</h2>
                  <p className="content text-lg text-gray-600">
                    Join a mission-driven team that's transforming education through cutting-edge AI technology
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-rocket text-blue-600 text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2">Innovation</h3>
                    <p className="text-sm text-gray-600">Work on cutting-edge AI technology</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-heart text-green-600 text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2">Impact</h3>
                    <p className="text-sm text-gray-600">Help millions of children succeed</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-users text-purple-600 text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2">Team</h3>
                    <p className="text-sm text-gray-600">Collaborate with world-class experts</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-graduation-cap text-yellow-600 text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2">Growth</h3>
                    <p className="text-sm text-gray-600">Continuous learning opportunities</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-8">
                {/* Software Engineer */}
                <div className="card border-l-4 border-blue-500">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                          <i className="fas fa-code text-blue-600 text-xl"></i>
                        </div>
                        <div>
                          <h3 className="title text-2xl font-bold mb-1">Software Engineer</h3>
                          <p className="text-gray-600 text-lg">Full-time • San Francisco, CA / Remote</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">React</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">TypeScript</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">Node.js</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">AWS</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">WebRTC</span>
                      </div>
                    </div>
                    <button className="btn btn-primary text-lg px-6 py-3">
                      <i className="fas fa-paper-plane mr-2"></i>
                      Apply Now
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">About the Role</h4>
                      <p className="text-sm text-gray-700">
                        Join our core engineering team to build scalable AI-powered language screening and training platforms. 
                        You'll work on our flagship products that serve 1.15M+ students in California's mandate.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Develop and maintain our React-based web applications</li>
                        <li>• Build real-time audio processing and speech recognition features</li>
                        <li>• Integrate with AI/ML models for language assessment</li>
                        <li>• Design and implement scalable backend services</li>
                        <li>• Collaborate with clinical and research teams</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Requirements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• 3+ years of full-stack development experience</li>
                        <li>• Strong proficiency in React, TypeScript, and Node.js</li>
                        <li>• Experience with audio processing and WebRTC</li>
                        <li>• Knowledge of AI/ML integration patterns</li>
                        <li>• Passion for education technology</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Research Engineer */}
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="title text-xl font-semibold mb-2">Research Engineer</h3>
                      <p className="text-gray-600 mb-2">Full-time • Berkeley, CA</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">Python</span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">PyTorch</span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">Speech Processing</span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">Research</span>
                      </div>
                    </div>
                    <button className="btn btn-primary">Apply Now</button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">About the Role</h4>
                      <p className="text-sm text-gray-700">
                        Work directly with our research team at UC Berkeley to develop cutting-edge speech AI models 
                        for child language assessment. Bridge the gap between research and production.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Implement and optimize speech recognition models for children</li>
                        <li>• Develop phoneme-level analysis algorithms</li>
                        <li>• Build data pipelines for clinical validation</li>
                        <li>• Collaborate with UCSF clinical partners</li>
                        <li>• Publish research papers and present at conferences</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Requirements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• PhD in Computer Science, Speech Processing, or related field</li>
                        <li>• Strong background in deep learning and speech processing</li>
                        <li>• Experience with PyTorch, TensorFlow, or similar frameworks</li>
                        <li>• Knowledge of clinical research methodologies</li>
                        <li>• Strong publication record preferred</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Research Scientist */}
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="title text-xl font-semibold mb-2">Research Scientist - Speech AI</h3>
                      <p className="text-gray-600 mb-2">Full-time • Berkeley, CA</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Speech Foundation Models</span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Phoneme Models</span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Research</span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Leadership</span>
                      </div>
                    </div>
                    <button className="btn btn-primary">Apply Now</button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">About the Role</h4>
                      <p className="text-sm text-gray-700">
                        Lead research initiatives to develop next-generation speech foundation models specifically 
                        designed for child language assessment. Drive innovation in our core AI technology.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Lead research on Speech Foundation Models for children</li>
                        <li>• Develop Phoneme Foundation Models for language assessment</li>
                        <li>• Design and conduct large-scale clinical studies</li>
                        <li>• Mentor junior researchers and engineers</li>
                        <li>• Collaborate with top-tier academic institutions</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Requirements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• PhD in Speech Processing, NLP, or related field</li>
                        <li>• 5+ years of research experience in speech AI</li>
                        <li>• Proven track record of high-impact publications</li>
                        <li>• Experience with large-scale model training</li>
                        <li>• Strong leadership and communication skills</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <p className="text-gray-600 mb-4">Don't see a role that fits? We're always looking for exceptional talent.</p>
                <div className="space-x-4">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-outline">
                    <i className="fas fa-envelope mr-2"></i>
                    Contact Us
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className="btn btn-primary">
                    <i className="fas fa-arrow-left mr-2"></i>
                    Back to Home
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return renderHomePage();
    }
  };

  const renderHomePage = () => (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="text-center">
            <h1 className="hero-title">
              AI Language Screening<br />
              <span className="highlight">for Every Child</span>
            </h1>
            <p className="hero-subtitle">
              Our clinically-validated AI solution makes language screening accessible, affordable, and scalable. 
              Developed in partnership with UCSF, supporting 1.15M+ students in California's mandate.
            </p>
            <div className="hero-buttons">
              <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('products'); }} className="btn btn-primary">
                <i className="fas fa-bullseye mr-2"></i>
                Start Screening
              </a>
              <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo'); }} className="btn btn-ghost">
                <i className="fas fa-play mr-2"></i>
                Watch Demo
              </a>
            </div>
            
            {/* Stats */}
            <div className="stats">
              <div className="stat-item">
                <span className="stat-number">15,000+</span>
                <span className="stat-label">Children Assessed</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1.15M+</span>
                <span className="stat-label">Students in CA Mandate</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">$4.7B</span>
                <span className="stat-label">Total Addressable Market</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">91%</span>
                <span className="stat-label">AI Accuracy</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">5min</span>
                <span className="stat-label">Assessment Time</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Features Preview */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="title text-3xl font-bold mb-4">Explore Our Products</h2>
            <div className="accent-bar" style={{margin: '0 auto'}}></div>
            <p className="content text-lg mb-8">
              Discover our comprehensive AI-powered language learning solutions
            </p>
            
            {/* Video Demo Section */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <i className="fas fa-play text-3xl text-blue-600"></i>
                </div>
                <h3 className="title text-2xl font-bold mb-2">Watch Our Demo</h3>
                <p className="content text-gray-600 mb-4">
                  See how our AI-powered platform works in just 2 minutes
                </p>
                <button onClick={(e) => { e.preventDefault(); handleNavClick('demo'); }} className="btn btn-primary">
                  <i className="fas fa-play mr-2"></i>
                  Watch Demo
                </button>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('products'); }} className="btn btn-primary text-xl px-8 py-4">
                <i className="fas fa-arrow-right mr-2"></i>
                Explore All Products
              </a>
              <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-outline text-xl px-8 py-4">
                <i className="fas fa-calendar mr-2"></i>
                Request a Formal Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Market Coverage */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="title text-3xl font-bold mb-4">Market Coverage</h2>
            <div className="accent-bar" style={{margin: '0 auto'}}></div>
            <p className="content text-lg">
              Leading the language screening revolution across California and expanding nationwide
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            <div className="card">
              <h3 className="title text-xl font-semibold mb-4">Current Market Presence</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="text-center">
                  <h4 className="title text-lg font-semibold mb-2">California</h4>
                  <p className="content text-sm mb-3">
                    <strong>1.15M+ Students</strong> in K-2 Mandate<br/>
                    <strong>$520M-1.04B</strong> Market Size<br/>
                    <strong>91%</strong> AI Accuracy Rate
                  </p>
                  <div className="flex justify-center space-x-4">
                    <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('products'); }} className="btn btn-primary">
                      <i className="fas fa-bullseye mr-2"></i>
                      View Products
                    </a>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('demo-request'); }} className="btn btn-outline">
                      <i className="fas fa-calendar mr-2"></i>
                      Request Demo
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <h3 className="title text-xl font-semibold mb-4">Expansion Plans</h3>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                  <div className="w-8 h-8 bg-yellow-200 rounded-full flex items-center justify-center mr-3">
                    <i className="fas fa-clock text-yellow-700"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-yellow-800">Phase 1 (2025)</h4>
                    <p className="text-sm text-gray-600">Texas, New York, Florida</p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                    <i className="fas fa-rocket text-blue-700"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800">Phase 2 (2026)</h4>
                    <p className="text-sm text-gray-600">National rollout to all 50 states</p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                  <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center mr-3">
                    <i className="fas fa-globe text-purple-700"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-800">Phase 3 (2027)</h4>
                    <p className="text-sm text-gray-600">International expansion</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>LinguaNest</h3>
            <p>AI-powered language ecosystem designed to help every child succeed in their language journey.</p>
          </div>
          <div className="footer-section">
            <h3>Connect</h3>
            <p>www.linguanest.org</p>
            <p>hello@linguanest.org</p>
            <p>Available September 2025</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 LinguaNest. All rights reserved. 🛡️ Built with ❤️ for children everywhere.</p>
        </div>
      </footer>
    </>
  );

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className="navbar-brand">
            <div className="logo">
              <i className="fas fa-graduation-cap"></i>
            </div>
            LinguaNest
          </a>
          <div className="navbar-menu">
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('home'); }} className={`navbar-link ${currentPage === 'home' ? 'active' : ''}`}>Home</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('products'); }} className={`navbar-link ${currentPage.startsWith('products') || currentPage.startsWith('screener') || currentPage.startsWith('pronunciation') || currentPage.startsWith('vocabulary') || currentPage.startsWith('phonological') || currentPage.startsWith('alphabetic') || currentPage.startsWith('reading') || currentPage.startsWith('processing') || currentPage.startsWith('memory') || currentPage.startsWith('foundations') || currentPage.startsWith('fluency') || currentPage.startsWith('mastery') ? 'active' : ''}`}>Products</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('pricing'); }} className={`navbar-link ${currentPage === 'pricing' ? 'active' : ''}`}>Pricing</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('why-us'); }} className={`navbar-link ${currentPage === 'why-us' ? 'active' : ''}`}>Why Us</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('about'); }} className={`navbar-link ${currentPage === 'about' ? 'active' : ''}`}>About</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('careers'); }} className={`navbar-link ${currentPage === 'careers' ? 'active' : ''}`}>Careers</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavClick('team'); }} className={`navbar-link ${currentPage === 'team' ? 'active' : ''}`}>Team</a>
          </div>
        </div>
      </nav>

      {renderPage()}
    </div>
  );
}

export default App;